import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import { useFulfilmentLocationContext } from '../../../components/FulfilmentLocationProvider';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Portal from '@material-ui/core/Portal';
import Typography from '@material-ui/core/Typography';
import UpdateSKUPositionDrawer from '../../../components/UpdateSKUPositionDrawer';
import LocationPositionDialog from './LocationPositionDialog';
import PositionProductList from './PositionProductList';
import BatchDialog from '@afosto/app-components-react/BatchDialog';
import BulkButton from '@afosto/components-react/BulkButton';
import BulkDrawer from '@afosto/app-components-react/BulkDrawer';
import { DataGrid } from '@afosto/app-components-react/DataGrid';
import DataGridPreview from '@afosto/app-components-react/DataGrid/DataGridPreview';
import DataList from '@afosto/app-components-react/DataList';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import formatDateTime from '@afosto/utils/date/formatDateTime';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import translations from './translations';

const DataListContainerGrid = props => <Grid container spacing={1} {...props} />;

const FulfilmentLocationPositions = ({ container }) => {
  const intl = useIntl();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const {
    fulfilmentLocation,
    fulfilmentPositions,
    showAddMultiplePositions,
    showUpdateSKUPosition,
    setFulfilmentPositions,
    setShouldLoadLocation,
    setShowAddMultiplePositions,
    setShowUpdateSKUPosition,
  } = useFulfilmentLocationContext();
  const [activePosition, setActivePosition] = useState(null);
  const [showExportPositionsDialog, setShowExportPositionsDialog] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const fetchPositions = async dataSource =>
    dataSourceRequest(
      dataSource,
      async (data, requestOptions) =>
        fulfilmentApi.listPositions(fulfilmentLocation.id, requestOptions),
      {
        transformData: data =>
          data.reduce(
            (acc, position) => [
              ...acc,
              {
                ...position,
                id: position.code,
                skus_list: [...new Set(position.skus.map(skuData => skuData.sku))],
              },
            ],
            [],
          ),
      },
    );

  const handleExportPositions = batch => {
    history.push(`/batches/${batch?.id}`);
  };

  const dataSource = useDataGrid(fetchPositions, {
    modules: ['dataProperties', 'pagination', 'activeItem', 'selection'],
    gridOptions: {
      dataExport: true,
    },
    dataExport: {
      allowExportView: false,
      onExport: () => {
        setShowExportPositionsDialog(true);
      },
    },
    dataProperties: {
      fixedProperties: [
        {
          key: 'code',
          name: intl.formatMessage({
            id: 'dataGrid.columns.fulfilmentLocations.code',
            defaultMessage: 'Preferred position',
            description: 'The column name for column code',
          }),
        },
        {
          key: 'skus_list',
          name: intl.formatMessage({
            id: 'dataGrid.columns.fulfilmentLocations.skus',
            defaultMessage: "SKU's",
            description: 'The column name for column skus',
          }),
          type: 'array',
        },
        {
          key: 'sorting',
          name: intl.formatMessage({
            id: 'dataGrid.columns.fulfilmentLocations.sorting',
            defaultMessage: 'Sorting',
            description: 'The column name for column sorting',
          }),
        },
        {
          key: 'created_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.fulfilmentLocations.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column created at',
          }),
          type: 'datetime',
        },
      ],
    },
    selection: {
      allowSelectAll: false,
      onAddSelection: selected => {
        setSelectedItems(oldItems => {
          const currentItemIds = oldItems.map(item => item.id);
          const newItems = selected.filter(item => !currentItemIds.includes(item.id));

          return [...oldItems, ...newItems];
        });
      },
      onRemoveSelection: selected => {
        const removedIds = selected.map(item => item.id);
        setSelectedItems(oldItems => oldItems.filter(item => !removedIds.includes(item.id)));
      },
    },
  });
  const dataSourceRef = useRef(dataSource);
  const { activeItem } = dataSource?.activeItem ?? {};

  useEffect(() => {
    dataSourceRef.current = dataSource;
  });

  useEffect(() => {
    let isMounted = true;

    if (activeItem) {
      const { data } = dataSourceRef?.current ?? {};
      const position = findObjectInArray(data, 'id', activeItem);

      if (position && isMounted) {
        setActivePosition(position);
      }
    } else if (isMounted) {
      setActivePosition(null);
    }

    return () => {
      isMounted = false;
    };
  }, [activeItem]);

  const handleAddMultiplePositions = async data => {
    try {
      const positions = data.reduce((acc, row) => {
        const [position] = row ? Object.values(row) : [];
        return [...acc, position?.toUpperCase()];
      }, []);
      await fulfilmentApi.updatePositionOrder(fulfilmentLocation.id, positions);

      setShouldLoadLocation(true);
      enqueueSnackbar(intl.formatMessage(translations.feedback.positionsAdded), {
        variant: 'success',
      });

      dataSource.reloadDataSource();
      setShowAddMultiplePositions(false);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.positionsNotAdded), {
        variant: 'error',
      });
    }
  };

  const handleDeletePositions = async () => {
    try {
      const positions = selectedItems.map(item => item.code);

      await fulfilmentApi.deletePositions(fulfilmentLocation?.id, positions);

      const newFulfilmentPositions = fulfilmentPositions.filter(
        position => !positions.includes(position.code),
      );

      setFulfilmentPositions(newFulfilmentPositions);

      enqueueSnackbar(intl.formatMessage(translations.feedback.positionsDeleted), {
        variant: 'success',
      });

      setSelectedItems([]);
      dataSource.selection.setSelectedItems([]);
      dataSource.pagination.setActivePage(0);
      dataSource.pagination.setPageCursor(null);
      dataSource.reloadDataSource();
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.positionsNotDeleted), {
        variant: 'error',
      });
    }
  };

  /**
   * Render a fulfilment position in the data list.
   * @param {object} fulfilmentPosition
   * @returns {*}
   */
  const renderFulfilmentPosition = fulfilmentPosition => (
    <Grid key={fulfilmentPosition?.code} item xs={12}>
      <Card>
        <CardActionArea
          onClick={() => dataSource.activeItem.setActiveItem(fulfilmentPosition?.code)}
        >
          <Box p={2}>
            <Typography variant="body1">{fulfilmentPosition.code}</Typography>
            <Typography variant="body2" color="textSecondary">
              {fulfilmentPosition.skus
                .slice(0, 5)
                .map(result => result.sku)
                .join(', ')}
            </Typography>
          </Box>
        </CardActionArea>
      </Card>
    </Grid>
  );

  /**
   * Render the skeleton of an fulfilment position in the data list.
   * @param {string} key
   * @returns {*}
   */
  const renderFulfilmentPositionSkeleton = key => (
    <Grid key={key} item xs={12}>
      <Card>
        <CardContent>
          <SkeletonBody lines={2} />
        </CardContent>
      </Card>
    </Grid>
  );

  const bulkActions = [
    {
      key: 'markInTransit',
      render: () => (
        <BulkButton onClick={handleDeletePositions} key="deletePositions">
          <Typography variant="button">
            {intl.formatMessage(translations.bulkActions.deletePositions)}
          </Typography>
        </BulkButton>
      ),
    },
  ];

  return (
    <>
      <Hidden implementation="js" mdUp>
        <DataList
          dataSource={dataSource}
          renderItem={renderFulfilmentPosition}
          renderSkeletonItem={renderFulfilmentPositionSkeleton}
          component={DataListContainerGrid}
          emptyMessage={intl.formatMessage(translations.emptyMessage)}
        />
        <LocationPositionDialog
          title={intl.formatMessage(translations.positionDialog.title)}
          isOpen={!!activePosition}
          onClose={() => setActivePosition(null)}
          locationPosition={activePosition}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid bulkActions={bulkActions} dataSource={dataSource} />
        <Portal container={container.current}>
          <DataGridPreview
            onClose={() => {
              setActivePosition(null);
              dataSource.activeItem.setActiveItem(null);
            }}
            open={activePosition !== null}
            title={intl.formatMessage(translations.positionDialog.title)}
          >
            {activePosition && <PositionProductList locationPosition={activePosition} />}
          </DataGridPreview>
        </Portal>
      </Hidden>
      <UpdateSKUPositionDrawer
        fulfilmentLocation={fulfilmentLocation}
        isOpen={showUpdateSKUPosition}
        onClose={() => setShowUpdateSKUPosition(false)}
        onUpdate={addAnotherProduct => {
          if (!addAnotherProduct) {
            setShowUpdateSKUPosition(false);
          }

          dataSource.reloadDataSource();
        }}
      />
      <BulkDrawer
        columns={[intl.formatMessage(translations.addMultiplePositionsDrawer.columns.position)]}
        isOpen={showAddMultiplePositions}
        onClose={() => setShowAddMultiplePositions(false)}
        onSubmit={handleAddMultiplePositions}
        title={intl.formatMessage(translations.addMultiplePositionsDrawer.title)}
      />
      <BatchDialog
        actionId="position_export"
        actionConfig={{
          location_id: fulfilmentLocation?.id,
        }}
        code="lcs"
        batchCreateLabel={intl.formatMessage(translations.exportPositions.batchCreateLabel)}
        batchFailedLabel={intl.formatMessage(translations.exportPositions.batchFailedLabel)}
        batchStartLabel={intl.formatMessage(translations.exportPositions.batchStartLabel)}
        batchValidationLabel={intl.formatMessage(translations.exportPositions.batchValidationLabel)}
        isOpen={showExportPositionsDialog}
        onClose={() => setShowExportPositionsDialog(false)}
        onFailed={() => dataSource.setShowDataExportSelection(true)}
        onFinish={handleExportPositions}
        reference={intl.formatMessage(translations.exportPositions.reference, {
          group: fulfilmentLocation?.name,
          value: formatDateTime(
            new Date(),
            undefined,
            {},
            {
              replaceDash: false,
            },
          ),
        })}
        title={intl.formatMessage(translations.exportPositions.title)}
      />
    </>
  );
};

FulfilmentLocationPositions.propTypes = {
  container: PropTypes.object,
};

FulfilmentLocationPositions.defaultProps = {
  container: undefined,
};

export default FulfilmentLocationPositions;
